import { IBank } from '../interfaces/banks'
import { ICompany } from '../interfaces/companies'
import { IBankAccount } from '../interfaces/bankAccounts'
import ICurrency from '../pages/currencies/currency.interface'
import { FormInstance, FormRule, message } from 'antd'
import { httpClient, ROOT_URL } from '../App'
import isURL from 'validator/lib/isURL'
import { CrudFilter } from '@refinedev/core'
import { IRemitter } from '../interfaces/remitter'
import { ICustomer } from '../interfaces/customer'
import { RuleObject } from 'rc-field-form/lib/interface'
import PaymentStatus from '../types/paymentStatus.type'
import PurposeOfRemittance from '../types/purposeOfRemittance.type'
import { Rule } from 'antd/es/form'

export const onlyLatinCharsDigitsSpecialSymbolsRule: FormRule = {
  pattern: /^[\sa-zA-Z0-9!@#$%^&*)(+=.,_-]*$/g,
  message: 'Only Latin letters, numbers or special characters are allowed',
  validateTrigger: 'onBlur',
}

export const websiteRegex: FormRule = {
  pattern: /^[\sa-zA-Z0-9!@#$%^&*)(:/+=.,_-]*$/g,
  message: 'Only Latin letters, numbers or special characters are allowed',
  validateTrigger: 'onBlur',
}

export const onlyLatinCharsDigitsRule: FormRule = {
  pattern: /^[a-zA-Z0-9\s]*$/,
  message: 'Special characters are not accepted',
  validateTrigger: 'onBlur',
}

export const onlyUppercaseNumbers: FormRule = {
  pattern: /^[A-Z0-9\s]*$/,
  message: 'Only uppercase letters and numbers are allowed',
  validateTrigger: 'onBlur',
}

export const onlyNumbersRule: FormRule = {
  pattern: /^[0-9]*$/,
  message: 'Only numbers are allowed',
  validateTrigger: 'onBlur',
}

export const noWhitespaceRule: FormRule = {
  pattern: /^\S*$/,
  message: 'No whitespace allowed',
  validateTrigger: 'onBlur',
}

export const customerRules = (customerData: ICustomer | undefined) => [
  () => ({
    validator() {
      if (customerData) {
        return Promise.resolve()
      }
      return Promise.reject(new Error('Please select customer'))
    },
  }),
]
export const bankRulesSimple = (bankData: IBank | undefined | null) => [
  () => ({
    validator() {
      if (bankData) {
        return Promise.resolve()
      }
      return Promise.reject(new Error('Please select bank'))
    },
    validateTrigger: 'onSubmit',
  }),
]

export const currencyRulesSimple = (currencyData: ICurrency | undefined) => [
  () => ({
    validator() {
      if (currencyData) {
        return Promise.resolve()
      }
      return Promise.reject(new Error('Please select currency'))
    },
    validateTrigger: 'onSubmit',
  }),
]

export type RequestButtonType = 'save' | 'send'

export const bankRules = (
  bankData: IBank | undefined | null,
  status: PaymentStatus
) => [
    () => ({
      validator() {
        if (bankData || status === PaymentStatus.DRAFT) {
          return Promise.resolve()
        }
        return Promise.reject(new Error('Please select bank'))
      },
    }),
  ]

export const currencyRules = (
  currencyData: ICurrency | null | undefined,
  status?: PaymentStatus
) => [
    () => ({
      validator() {
        if (currencyData || status === PaymentStatus.DRAFT) {
          return Promise.resolve()
        }
        return Promise.reject(new Error('Please select currency'))
      },
    }),
  ]

export const bankAccountRules = (
  bankAccountData: IBankAccount | undefined | null,
  status: PaymentStatus
) => [
    () => ({
      validator() {
        if (bankAccountData || status === PaymentStatus.DRAFT) {
          return Promise.resolve()
        }
        return Promise.reject(new Error('Please select bank account'))
      },
    }),
  ]
export const dateOfTransactionRules = [
  {
    required: true,
    message: 'Please select date of transaction',
  },
]

export const amountRules = (status?: PaymentStatus, required = false) => [
  {
    required: status === PaymentStatus.PENDING,
    pattern: new RegExp(/^\d+(\.\d\d?)?$/),
    message: 'Please enter amount',
    validator: (_: RuleObject, value: string | number | undefined | null) => {
      return (!required &&
        status === PaymentStatus.DRAFT &&
        typeof value !== 'number' &&
        typeof value !== 'string') ||
        (value && Number(value) > 0)
        ? Promise.resolve()
        : Promise.reject()
    },
  },
]

export const paymentRequestServiceProviderRules = (
  serviceProviderData: ICompany | undefined | null,
  status?: PaymentStatus
) => [
    () => ({
      validator() {
        if (
          serviceProviderData ||
          status === PaymentStatus.DRAFT ||
          status === PaymentStatus.PENDING ||
          status === PaymentStatus.REJECTED
        ) {
          return Promise.resolve()
        }
        return Promise.reject(new Error(`Choose service provider`))
      },
    }),
  ]

export const paymentRequestPurposeOfRemittanceRules = (
  status?: PaymentStatus
): Rule[] => [
    {
      validator: async (_, value: PurposeOfRemittance) => {
        if (
          !value &&
          (status === PaymentStatus.COMPLETED ||
            status === PaymentStatus.APPROVED)
        ) {
          return Promise.reject(new Error(`Choose purpose of remittance`))
        }
        return Promise.resolve()
      },
    },
  ]

export const companyRules = (
  companyData: ICompany | undefined | null,
  status?: PaymentStatus,
  entityName?: string
) => [
    () => ({
      validator() {
        if (companyData || status === PaymentStatus.DRAFT) {
          return Promise.resolve()
        }
        return Promise.reject(
          new Error(`Please select ${entityName ?? 'company'}`)
        )
      },
      validateTrigger: 'onSubmit',
    }),
  ]

export const remitterRules = (
  remitterData: IRemitter | undefined | null,
  status?: PaymentStatus
) => companyRules(remitterData, status, 'remitter')

export const serviceProviderRules = (
  serviceProviderData: ICompany | undefined | null,
  status?: PaymentStatus
) => companyRules(serviceProviderData, status, 'service provider')

export const clearFieldErrorMessageOnChange =
  (form?: FormInstance) => (e: any) => {
    if (form === undefined) {
      return null
    }
    if (e instanceof Object) {
      const name = Object.keys(e)[0]
      form.setFields([
        {
          name,
          errors: [],
        },
      ])
    }
  }

export const isUserIdAvailableApi = async (userId: string) => {
  return httpClient
    .get(`${ROOT_URL}/api/v1/auth/check-user-id`, {
      params: { userId },
    })
    .then((res) => {
      return res.data
    })
    .catch((error) => {
      message.error(`${error.response.status} ${error.response.statusText}`)
      return false
    })
}

export const websiteRules: FormRule[] = [
  websiteRegex,
  ({ getFieldValue }) => ({
    validator() {
      const value = getFieldValue('website') ?? ''
      if (isURL(value) || value.length === 0) {
        return Promise.resolve()
      }
      return Promise.reject(new Error('Invalid website'))
    },
    validateTrigger: 'onBlur',
  }),
  {
    required: true,
    message: 'This field is required',
    validateTrigger: 'onBlur',
  },
  {
    max: 256,
    message: 'Maximum 256 characters',
    validateTrigger: 'onBlur',
  },
]
export const trimFormFieldValue = (name: string, form?: FormInstance) => () => {
  if (form) {
    const value = form.getFieldValue(name)
    if (value) {
      form.setFieldValue(name, value.trim())
    }
  }
}

export const companySearchFieldNameFilter: CrudFilter = {
  field: 'searchFields',
  operator: 'eq',
  value: ['name'],
}

export const getFileListFromEvent = (
  e: { fileList?: string[] } | string[]
): string[] | undefined => (Array.isArray(e) ? e : e?.fileList)

export const companyNameRules: FormRule[] = [
  {
    pattern: /^[a-zA-Z0-9@&\-.() ]*$/,
    message: 'Only Latin letters, numbers, and @ & - . ( ) are allowed',
    validateTrigger: 'onBlur',
  },
  { required: true, message: 'This field is required' },
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve()
      const trimmed = (value || '').trim()
      if (trimmed.length < 4) {
        return Promise.reject('Minimum 4 characters')
      }
      return Promise.resolve()
    },
  },
  {
    max: 120,
    message: 'Maximum 120 characters',
    validateTrigger: 'onBlur',
  },
]

export const nameRules: FormRule[] = [
  {
    required: true,
    message: 'This field is required',
    validateTrigger: 'onBlur',
  },
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve()
      const trimmed = (value || '').trim()
      if (trimmed.length < 4) {
        return Promise.reject('Minimum 4 characters')
      }
      return Promise.resolve()
    },
  },
  {
    max: 120,
    message: 'Maximum 120 characters',
    validateTrigger: 'onBlur',
  },
]

export const addressRules: FormRule[] = [
  onlyLatinCharsDigitsSpecialSymbolsRule,
  {
    required: true,
    validateTrigger: 'onBlur',
    message: 'This field is required',
    whitespace: true,
  },
  {
    max: 256,
    message: 'Maximum 256 characters',
    validateTrigger: 'onBlur',
  },
]

export const directorNameRules: FormRule[] = [
  onlyLatinCharsDigitsRule,
  {
    required: true,
    message: 'This field is required',
    whitespace: true,
  },
  {
    max: 256,
    message: 'Maximum 256 characters',
    validateTrigger: 'onBlur',
  },
]

export const cityRules: FormRule[] = [
  onlyLatinCharsDigitsRule,
  { required: true, message: 'This field is required' },
  {
    min: 2,
    message: 'Minimum 2 characters',
  },
  {
    max: 256,
    message: 'Maximum 256 characters',
    validateTrigger: 'onBlur',
  },
]

export const tinRules: FormRule[] = [
  onlyUppercaseNumbers,
  noWhitespaceRule,
  { required: true, message: 'This field is required' },
  {
    min: 4,
    message: 'Minimum 4 characters',
  },
  {
    max: 20,
    message: 'Maximum 20 characters',
    validateTrigger: 'onBlur',
  },
]

export const phoneNumberRules: FormRule[] = [
  { required: true, message: 'This field is required' },
  {
    min: 6,
    message: 'Minimum 6 characters',
    validateTrigger: 'onBlur',
  },
  {
    max: 21,
    message: 'Maximum 21 characters',
    validateTrigger: 'onBlur',
  },
]

export const emailRules: FormRule[] = [
  noWhitespaceRule,
  { type: 'email', validateTrigger: 'onBlur' },
]

export const addressWalletRules: FormRule[] = [
  onlyLatinCharsDigitsRule,
  {
    required: true,
    message: 'This field is required',
    validateTrigger: 'onBlur',
  },
  {
    min: 4,
    message: 'Minimum 4 characters',
    validateTrigger: 'onBlur',
  },
  {
    max: 256,
    message: 'Maximum 256 characters',
    validateTrigger: 'onBlur',
  },
]

export const accountNumberRules: FormRule[] = [
  onlyLatinCharsDigitsRule,
  {
    required: true,
    message: 'This field is required',
  },
  {
    min: 5,
    message: 'Minimum 5 characters',
  },
  {
    max: 35,
    message: 'Maximum 35 characters',
    validateTrigger: 'onBlur',
  },
]

export const abaRountingNumberRules: FormRule[] = [
  onlyLatinCharsDigitsRule,
  {
    min: 4,
    message: 'Minimum 4 characters',
    validateTrigger: 'onBlur',
  },
  {
    max: 25,
    message: 'Maximum 25 characters',
    validateTrigger: 'onBlur',
  },
]

export const bankStateBranchNumberRules: FormRule[] = [
  onlyLatinCharsDigitsRule,
  {
    min: 4,
    message: 'Minimum 4 characters',
    validateTrigger: 'onBlur',
  },
  {
    max: 25,
    message: 'Maximum 25 characters',
    validateTrigger: 'onBlur',
  },
]

export const branchNumberRules: FormRule[] = [
  onlyLatinCharsDigitsRule,
  { required: true, message: 'This field is required' },
  {
    min: 3,
    message: 'Minimum 3 characters',
    validateTrigger: 'onBlur',
  },
  {
    max: 8,
    message: 'Maximum 8 characters',
    validateTrigger: 'onBlur',
  },
]

export const postalCodeRules: FormRule[] = [
  { required: true, message: 'This field is required', whitespace: true },
  {
    min: 2,
    message: 'Minimum 2 characters',
    validateTrigger: 'onBlur',
  },
  {
    max: 20,
    message: 'Maximum 20 characters',
    validateTrigger: 'onBlur',
  },
]
